import { Link, Text, View } from '@react-pdf/renderer';
import logo from '@ui/assets/images/CSC/CENTURY_SPRING_RGB_PRIMARY_LOGO_AW.png';

import { usePDFContext } from '../PDFProvider';
import { EmployeeInformation } from './components/EmployeeInformation';
import { Header } from './components/Header';
import { Logo } from './components/Logo';
import { List } from './components/misc';
import { Page } from './components/Page';
import { CustomerAddress } from './components/QuoteDetails/CustomerAddress';
import { CustomerContact } from './components/QuoteDetails/CustomerContact';
import { QuoteDetails } from './components/QuoteDetails/QuoteDetails';
import { QuoteInformation } from './components/QuoteDetails/QuoteInformation';
import { QuoteId } from './components/QuoteId';
import { QuoteLineForPart, QuoteLineForServices } from './components/QuoteLine';
import { PartDescription } from './components/QuoteLine/PartDescription';
import { PartId } from './components/QuoteLine/PartId';
import { PartSpecs } from './components/QuoteLine/PartSpecs';
import { SiteInformation } from './components/SiteInformation';
import { Specs } from './components/Specs';
import { styles } from './components/styles';

const CSC_SITE_INFORMATION = [
  '5959 Triumph Street',
  'Commerce CA 90040',
  'Phone (800) 237-5225',
  'Fax 213-749-8569',
];

export function CSCCustomQuoteDocument() {
  const { quoteLines } = usePDFContext();

  return (
    <Page>
      <Header>
        <Logo source={logo} extraStyles={[{ height: '42px' }]} />

        <SiteInformation
          name="Century Spring Corp. / MW Ind."
          items={CSC_SITE_INFORMATION}
        />

        <QuoteId ecommerceUri="https://www.centuryspring.com" />
      </Header>

      <QuoteDetails>
        <CustomerAddress />

        <CustomerContact />

        <QuoteInformation />
      </QuoteDetails>
      <View style={styles.row}>
        <View style={[styles.fullRow, styles.textSmall]}>
          <Text>
            Please Note: There is an additional fee per report when required:
            $250 for AS9102 and $1500 for PPAP III
          </Text>
          <Text style={styles.bold}>
            Please Note: Manufacturing lead time begins upon receipt of the
            correct PO (Price, Quantity and Due Date). The quoted lead time is
            the ship date from CSC location. Please add at least one week for
            transit. Thank you.
          </Text>
          <Text style={styles.bold}>
            Customer samples sent to us for quotation will be discarded after 2
            months or on quotation expiration date, please let us know if you
            need your sample spring back asap
          </Text>
          <Text style={styles.bold}>
            ***Based on raw material availability, the lead times may change at
            receipt & after your order confirmation***
          </Text>
        </View>
      </View>

      {quoteLines.map((line) => {
        const { part, ...lineProps } = line;
        if (!part) {
          return <QuoteLineForServices {...line} showDescription />;
        }

        return (
          <QuoteLineForPart
            key={line.id}
            {...lineProps}
            part={part}
            showDrawing
          >
            <PartId part={part} showCustomerPartId />

            <PartDescription description={part.description} />
            {part.partSpecs && <PartSpecs specs={part.partSpecs} />}
            <PartSpecs specs={line.specs} />
          </QuoteLineForPart>
        );
      })}

      <Specs />

      <EmployeeInformation />

      <View style={styles.row}>
        <View style={[styles.fullRow, styles.textSmall]}>
          <List>
            <Text style={styles.bold}>CONDITIONS OF SALE:</Text>
            <Text style={styles.bold}>
              1. NO CONTRACT SHALL EXIST UNTIL SELLER RECEIVES ORDER FOR
              DELIVERY PLACED BY BUYER.
            </Text>
            <Text style={styles.bold}>
              2. PRICES ARE SUBJECT TO CHANGE TO CONFORM TO PRICES PREVAILING AT
              TIME OF SHIPMENT.
            </Text>
            <Text style={styles.bold}>
              3. PRICES ARE SUBJECT TO PRODUCTION RELEASES. WHERE REVISIONS
              RESULT IN HIGHER COSTS PRICES ARE SUBJECT TO CHANGE.
            </Text>
            <Text style={styles.bold}>
              4. ALL PRICES ARE SUBJECT TO CHANGE TO CONFORM TO ALL STATE AND
              FEDERAL LAWS.
            </Text>
            <Text style={styles.bold}>
              5. CHARGES MADE FOR TOOLS AND DIES DO NOT CONVEY THE RIGHT TO
              REMOVE THEM FROM OUR FACTORY.
            </Text>
            <Text style={styles.bold}>
              6. IF ANY MATERIAL IS PROVED DEFECTIVE, SELLERS LIABILITY IS
              LIMITED TO REPLACEMENT ONLY AND SELLER WILL NOT PAY ANY CLAIM FOR
              LABOR ON OR FOR DAMAGE BY REASON OF ITS USE.
            </Text>
            <Text style={styles.bold}>
              7. THE SELLER SHALL NOT BE LIABLE FOR INFRINGEMENT OF ANY PATENTS
              REQUIRED TO BE USED BY THE SPECIFICATIONS OF THE BUYER AND THE
              BUYER AGREES TO SAVE THE SELLER HARMLESS FOR ALL JUDGMENTS,
              DECREES, COSTS AND OTHER LEGAL EXPENSES RESULTING FROM ANY SUITS
              AND AGREES TO DEFEND SELLER SHOULD SUCH A SUIT BE INSTITUTED.
            </Text>
            <Text style={styles.bold}>
              8. GOODS SHALL BE INSPECTED WHEN RECEIVED AND THE SELLER IS NOT
              LIABLE FOR ANY DEFECTS UNLESS NOTIFIED WITHIN 30 DAYS AFTER
              RECEIPT BY THE BUYER.
            </Text>
            <Text style={styles.bold}>
              9. WE RESERVE THE RIGHT TO OVER AND UNDER SHIP BY ANY AMOUNT NOT
              TO EXCEED TEN PERCENT OF SPECIFIED QUANTITY.
            </Text>
            <Text style={styles.bold}>
              10. ALL AGREEMENTS ARE CONTINGENT UPON STRIKES, ACCIDENTS AND
              OTHER DELAYS UNAVOIDABLE OR BEYOND OUR CONTROL.
            </Text>
            <Text style={styles.bold}>
              11. ABOVE PRICING INCLUDES SET-UP FEE (OF $500.00 PLUS LABOR &
              MATERIAL), FREIGHT OR SALES TAX NOT INCLUDED.
            </Text>
            <Text style={styles.bold}>
              12. PRICING FOR SINGLE LOT SHIPMENT ONLY.
            </Text>
            <Text style={styles.bold}>
              13. UNLESS OTHERWISE SPECIFIED, THIS QUOTE IS TO S.M.I.
              TOLERANCES. BLOCK TOLERANCES DO NOT APPLY TO SPRING MANUFACTURE.
            </Text>
            <Text style={styles.bold}>
              14. SPECIAL ORDERS, ONCE IN PRODUCTION, CANNOT BE CANCELLED.
            </Text>
            <Text style={styles.bold}>
              15. PLEASE REFER TO QUOTE NUMBER WHEN ORDERING.
            </Text>
            <Text style={styles.bold}>
              16. IF A SAMPLE HAS BEEN SUBMITTED FOR PRICING, MATERIAL TYPE MUST
              BE SPECIFIED BY CUSTOMER. CENTURY DOES NO METALLURGICAL TESTING
              AND IS NOT RESPONSIBLE FOR APPLICATION.
            </Text>
            <Text style={styles.bold}>
              17. TO AVOID A DELAY IN PROCESSING YOUR ORDER, PLEASE CLARIFY
              QUALITY REQUIREMENTS AS SOON AS POSSIBLE. QUALITY REQUIREMENTS NOT
              ESTABLISHED DURING QUOTING MAY AFFECT PRICING AND LEAD TIME
            </Text>
          </List>
          <Text>{'\n'}</Text>
          <Text>{'\n'}</Text>
          <Text>{'\n'}</Text>
          <Text>View Your Order Status & Tracking Online:</Text>
          <Text>
            For updates on your order status and tracking information, log in to
            your account on MWComponents.com using the same email address
            associated with your order. Navigate to My Account, then select
            Orders. If you do not already have an account, you can create one.
            Orders associated with the account email address will automatically
            populate.
          </Text>
          <Text>{'\n'}</Text>
          <Text>{'\n'}</Text>
          <Text>{'\n'}</Text>
          <Text style={{ textAlign: 'center' }}>
            Standard terms and conditions can be found on{' '}
            <Link href="https://www.mwcomponents.com/mw-industries-terms-and-conditions">
              mwcomponents.com
            </Link>{' '}
            https://www.mwcomponents.com/mw-industries-terms-and-conditions
          </Text>
        </View>
      </View>
    </Page>
  );
}
